(() => {
  "use strict";
  if (customElements.get("saber-your-recommendations")) return;

  const ID = /^\d{1,24}$/;
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/i;
  const MAX_CARDS = 8;
  const MAX_HISTORY = 48;
  const MAX_LOOKUPS = MAX_HISTORY;

  class SaberYourRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.generation = 0;
      this.lookups = new Set();
      this.controllers = new Set();
      this.historyGrid = this.querySelector("[data-history-grid]");
      this.relatedGrid = this.querySelector("[data-recommendation-grid]");
      this.historyStatus = this.querySelector("[data-history-status]");
      this.relatedStatus = this.querySelector("[data-recommendation-status]");
      this.searches = this.querySelector("[data-recommendation-searches]");
      this.searchStatus = this.querySelector("[data-search-status]");
      this.clearButton = this.querySelector("[data-clear-viewed]");
      this.moreButton = this.querySelector("[data-history-more]");
      this.historyFooter = this.querySelector("[data-history-footer]");
      this.historyProgress = this.querySelector("[data-history-progress]");
      this.views = [];
      this.loadedCards = [];
      this.attemptedIds = new Set();
      this.loadingHistory = false;
      this.onMore = () => this.loadHistoryBatch(true);
      this.onRemove = event => {
        const button = event.target.closest("[data-remove-viewed]");
        if (!button || !this.historyGrid?.contains(button) || !ID.test(button.dataset.removeViewed || "") || typeof this.history?.remove !== "function") return;
        const before = this.generation;
        this.history.remove(button.dataset.removeViewed);
        if (before === this.generation) this.refresh(this.history.snapshot());
      };
      this.onReady = () => this.connectHistory();
      this.onClear = () => {
        if (!this.history) return;
        const before = this.generation;
        this.history.clear();
        if (before === this.generation) this.refresh(this.history.snapshot());
      };
      this.clearButton?.addEventListener("click", this.onClear);
      this.moreButton?.addEventListener("click", this.onMore);
      this.historyGrid?.addEventListener("click", this.onRemove);
      document.addEventListener("saber:viewed-history-ready", this.onReady);
      this.connectHistory();
    }

    disconnectedCallback() {
      document.removeEventListener("saber:viewed-history-ready", this.onReady);
      this.clearButton?.removeEventListener("click", this.onClear);
      this.moreButton?.removeEventListener("click", this.onMore);
      this.historyGrid?.removeEventListener("click", this.onRemove);
      this.unsubscribe?.();
      this.abortRequests();
      this.history = null;
      this.unsubscribe = null;
      this.started = false;
    }

    connectHistory() {
      const history = window.SaberViewedHistory;
      if (!history || typeof history.snapshot !== "function" || this.history === history) return;
      this.unsubscribe?.();
      this.history = history;
      if (typeof history.subscribe === "function") {
        this.unsubscribe = history.subscribe(snapshot => this.refresh(snapshot));
      }
      this.refresh(history.snapshot());
    }

    abortRequests() {
      this.generation += 1;
      this.controllers?.forEach(controller => controller.abort());
      this.controllers?.clear();
    }

    sameOriginURL(raw) {
      try {
        const url = new URL(raw, location.origin);
        return url.origin === location.origin && !url.username && !url.password && ["http:", "https:"].includes(url.protocol) ? url : null;
      } catch { return null; }
    }

    productURL(raw, handle) {
      const url = this.sameOriginURL(raw);
      return url && HANDLE.test(handle) && url.pathname.endsWith(`/products/${handle}`) ? url : null;
    }

    contextMatches(node) {
      try {
        const context = JSON.parse(document.getElementById("SaberRecommendationsContext")?.getAttribute("data-context") || "null");
        if (!context || !context.shop || !context.customer || !node) return false;
        return ["shop", "customer", "company", "location"].every(key =>
          node.dataset[`context${key[0].toUpperCase()}${key.slice(1)}`] === String(context[key] ?? ""));
      } catch { return false; }
    }

    cardElement(item, id) {
      if (!item || !ID.test(id) || item.dataset.recommendationProductId !== id) return null;
      const cards = item.querySelectorAll("product-card[data-product-id]");
      if (cards.length !== 1 || cards[0].dataset.productId !== id) return null;
      // Only native same-origin responses reach this point. Keep executable or
      // document-level markup out of the fragment and avoid duplicate form IDs.
      if (item.querySelector("script, style, iframe, object, embed, base, form, link, meta[http-equiv]")) return null;
      for (const node of [item, ...item.querySelectorAll("*")]) {
        for (const attribute of [...node.attributes]) {
          if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
        }
      }
      for (const anchor of item.querySelectorAll("a[href]")) {
        const href = anchor.getAttribute("href") || "";
        if (/^(tel:|mailto:)/i.test(href)) continue;
        if (!this.sameOriginURL(href)) anchor.removeAttribute("href");
      }
      return document.importNode(item, true);
    }

    async fetchHTML(url) {
      const controller = new AbortController();
      this.controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url.href, { credentials: "same-origin", cache: "no-store", headers: { Accept: "text/html" }, signal: controller.signal });
        if (!response.ok || (response.url && !this.sameOriginURL(response.url))) return null;
        const type = response.headers?.get("content-type");
        if (type && !type.includes("text/html")) return null;
        const html = await response.text();
        if (!html || html.length > 600000) return null;
        return { html, url: response.url || url.href };
      } catch { return null; }
      finally {
        clearTimeout(timeout);
        this.controllers.delete(controller);
      }
    }

    async viewedCard(view) {
      const id = String(view?.id || "");
      const handle = String(view?.handle || "");
      if (!ID.test(id) || !HANDLE.test(handle)) return null;
      if (!this.lookups.has(id) && this.lookups.size >= MAX_LOOKUPS) return null;
      this.lookups.add(id);
      const root = this.sameOriginURL(this.dataset.rootUrl || "/");
      if (!root) return null;
      const url = new URL(`${root.pathname.replace(/\/$/, "")}/products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set("view", "viewed-card");
      if (ID.test(String(view.variantId || ""))) url.searchParams.set("variant", view.variantId);
      const result = await this.fetchHTML(url);
      if (!result || !this.productURL(result.url, handle)) return null;
      const parsed = new DOMParser().parseFromString(result.html, "text/html");
      const wrappers = parsed.querySelectorAll("[data-saber-viewed-card]");
      if (wrappers.length !== 1) return null;
      const wrapper = wrappers[0];
      if (!this.contextMatches(wrapper) || wrapper.dataset.productId !== id || wrapper.dataset.productHandle !== handle || !this.productURL(wrapper.dataset.productUrl, handle)) return null;
      const item = this.cardElement(wrapper.querySelector("li[data-recommendation-product-id]"), id);
      return item ? { id, handle, item, vendor: wrapper.dataset.productVendor || "", type: wrapper.dataset.productType || "" } : null;
    }

    async mapCards(views) {
      const results = new Array(views.length);
      let next = 0;
      const generation = this.generation;
      await Promise.all(Array.from({ length: Math.min(3, views.length) }, async () => {
        while (next < views.length && generation === this.generation) {
          const index = next++;
          try { results[index] = await this.viewedCard(views[index]); }
          catch { results[index] = null; }
        }
      }));
      return results.filter(Boolean);
    }

    setStatus(node, text) {
      if (node) { node.textContent = text; node.hidden = !text; }
    }

    renderSearches(snapshot, cards = []) {
      if (!this.searches) return;
      this.searches.replaceChildren();
      const terms = [...(Array.isArray(snapshot.searches) ? snapshot.searches.map(value => value?.query) : []), ...cards.flatMap(card => [card.vendor, card.type])];
      const seen = new Set();
      for (const term of terms) {
        if (typeof term !== "string") continue;
        const clean = term.trim().replace(/\s+/g, " ").slice(0, 120);
        const key = clean.toLocaleLowerCase();
        // Account/help navigation searches are not useful product inspiration.
        if (clean.length < 2 || seen.has(key) || /^(?:(?:submit|request|track|my)\s+)?(?:return(?:s| request)?|refund(?:s)?|purchase order|order status|credit application|contact(?: us)?|log(?:in| out)|sign[ -]?in|repair parts finder)$/i.test(clean)) continue;
        const url = this.sameOriginURL(this.dataset.searchUrl || "/search");
        if (!url) continue;
        url.searchParams.set("q", clean);
        url.searchParams.set("type", "product");
        url.searchParams.set("options[prefix]", "last");
        const anchor = document.createElement("a");
        anchor.className = "button button-outline";
        anchor.href = url.pathname + url.search;
        anchor.textContent = clean;
        this.searches.append(anchor);
        seen.add(key);
        if (seen.size >= 4) break;
      }
      this.setStatus(this.searchStatus, seen.size ? "A few ideas from the products and brands you’ve explored." : "Search for a product, brand or part number.");
    }

    async relatedCards(seedId, excluded) {
      const url = this.sameOriginURL(this.dataset.recommendationsUrl || "/recommendations/products");
      if (!url || !ID.test(seedId)) return [];
      url.searchParams.set("section_id", "saber-recommendation-results");
      url.searchParams.set("product_id", seedId);
      url.searchParams.set("limit", String(MAX_CARDS));
      url.searchParams.set("intent", "related");
      const result = await this.fetchHTML(url);
      if (!result || this.sameOriginURL(result.url)?.pathname !== url.pathname) return [];
      const parsed = new DOMParser().parseFromString(result.html, "text/html");
      const wrappers = parsed.querySelectorAll('[data-saber-recommendation-results][data-performed="true"]');
      if (wrappers.length !== 1 || !this.contextMatches(wrappers[0])) return [];
      const wrapper = wrappers[0];
      const found = [];
      const seen = new Set(excluded);
      for (const item of wrapper.querySelectorAll("li[data-recommendation-product-id]")) {
        const id = item.dataset.recommendationProductId || "";
        const handle = item.dataset.productHandle || "";
        if (!ID.test(id) || seen.has(id) || !this.productURL(item.dataset.productUrl, handle)) continue;
        const card = this.cardElement(item, id);
        if (!card) continue;
        seen.add(id);
        found.push(card);
        if (found.length >= MAX_CARDS) break;
      }
      return found;
    }

    historyViews(snapshot) {
      const seen = new Set();
      return (Array.isArray(snapshot.views) ? snapshot.views : []).filter(view => {
        const id = String(view?.id || "");
        if (!ID.test(id) || !HANDLE.test(String(view?.handle || "")) || seen.has(id)) return false;
        seen.add(id); return true;
      }).slice(0, MAX_HISTORY);
    }

    viewKey(view) {
      return JSON.stringify([String(view.id), view.handle, String(view.variantId || ""), view.viewedAt]);
    }

    updateHistoryControls() {
      const remaining = this.views.some(view => !this.attemptedIds.has(String(view.id)));
      if (this.clearButton) this.clearButton.hidden = !this.views.length && !(this.latestSnapshot?.searches?.length);
      if (this.historyFooter) this.historyFooter.hidden = !this.views.length;
      if (this.moreButton) {
        this.moreButton.hidden = !remaining;
        this.moreButton.disabled = this.loadingHistory;
        this.moreButton.textContent = this.loadingHistory ? "Loading products…" : "Show more viewed products";
        this.moreButton.setAttribute("aria-busy", String(this.loadingHistory));
      }
      this.historyGrid?.setAttribute("aria-busy", String(this.loadingHistory));
      if (this.historyProgress) {
        this.historyProgress.textContent = this.views.length ? `Showing ${this.loadedCards.length} of ${this.views.length} previously viewed products.` : "";
      }

    }

    prepareHistoryCard(card) {
      if (typeof this.history?.remove !== "function") return card.item;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "ss-recommendations-remove";
      button.dataset.removeViewed = card.id;
      button.textContent = "Remove from viewed history";
      const title = card.item.querySelector(".card-heading")?.textContent.trim() || "this product";
      button.setAttribute("aria-label", `Remove ${title} from viewed history`);
      card.item.append(button);
      return card.item;
    }

    async loadHistoryBatch(moveFocus = false) {
      if (this.loadingHistory || !this.views.length) return;
      const batch = this.views.filter(view => !this.attemptedIds.has(String(view.id))).slice(0, MAX_CARDS);
      if (!batch.length) return;
      const generation = this.generation;
      this.loadingHistory = true;
      this.updateHistoryControls();
      if (!this.loadedCards.length) this.setStatus(this.historyStatus, "Loading the current product details…");
      const cards = await this.mapCards(batch);
      if (generation !== this.generation || !this.isConnected) return;
      batch.forEach(view => this.attemptedIds.add(String(view.id)));
      this.loadingHistory = false;
      const shown = new Set(this.loadedCards.map(card => card.id));
      const added = cards.filter(card => {
        if (shown.has(card.id)) return false;
        shown.add(card.id);
        return true;
      });
      this.loadedCards.push(...added);
      if (added.length && this.historyGrid) {
        this.historyGrid.append(...added.map(card => this.prepareHistoryCard(card)));
        this.historyGrid.hidden = false;
        this.history?.hydrate?.(this.historyGrid);
      }
      this.setStatus(this.historyStatus, this.loadedCards.length ? (this.loadedCards.length < this.attemptedIds.size ? "Some previously viewed products are no longer available to display." : "") : "Those products are no longer available to display. Explore this week’s products or choose a department below.");
      this.renderSearches(this.latestSnapshot, this.loadedCards);
      this.updateHistoryControls();
      if (moveFocus) {
        const target = added[0]?.item.querySelector(".card-heading a[href]");
        if (target) target.focus({ preventScroll: true });
        else if (this.moreButton?.hidden) this.focusHistoryHeading();
      }
    }

    focusHistoryHeading() {
      const heading = this.historyGrid?.closest("section")?.querySelector("h2");
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    }

    async refreshRelated() {
      const generation = this.generation;
      const seed = this.loadedCards[0];
      this.relatedGrid?.replaceChildren();
      if (this.relatedGrid) this.relatedGrid.hidden = true;
      if (!seed) { this.setStatus(this.relatedStatus, this.views.length ? "Explore Top Picks and Weekly Finds below for more ideas." : "View a few products to discover related ideas here."); return; }
      this.setStatus(this.relatedStatus, "Finding ideas from your recent views…");
      const related = await this.relatedCards(seed.id, new Set(this.loadedCards.map(card => card.id)));
      if (generation !== this.generation || !this.isConnected) return;
      if (related.length && this.relatedGrid) {
        this.relatedGrid.append(...related);
        this.relatedGrid.hidden = false;
        this.history?.hydrate?.(this.relatedGrid);
      }
      this.setStatus(this.relatedStatus, related.length ? "" : "More ideas are waiting in Top Picks and Weekly Finds below.");
    }

    async refresh(snapshot = {}) {
      const views = this.historyViews(snapshot);
      const nativeStamp = document.getElementById("SaberRecommendationsContext")?.getAttribute("data-context") || "";
      const sameScope = this.initialized && this.scope === snapshot.scope && nativeStamp && this.nativeStamp === nativeStamp;
      const previousKeys = new Set(this.views.map(view => this.viewKey(view)));
      const unchangedViews = sameScope && this.views.length === views.length && views.every((view, index) => this.viewKey(view) === this.viewKey(this.views[index]));
      const removedOnly = sameScope && this.views.length === views.length + 1 && views.every(view => previousKeys.has(this.viewKey(view)));
      this.latestSnapshot = snapshot;
      if (unchangedViews) {
        this.renderSearches(snapshot, this.loadedCards);
        this.updateHistoryControls();
        return;
      }
      if (removedOnly) {
        const focusedId = document.activeElement?.dataset?.removeViewed;
        const focusedIndex = this.loadedCards.findIndex(card => card.id === focusedId);
        this.abortRequests();
        const removalGeneration = this.generation;
        this.loadingHistory = false;
        this.views = views;
        const kept = new Set(views.map(view => String(view.id)));
        this.loadedCards = this.loadedCards.filter(card => {
          if (kept.has(card.id)) return true;
          card.item.remove();
          this.attemptedIds.delete(card.id);
          return false;
        });
        if (this.historyGrid) this.historyGrid.hidden = !this.loadedCards.length;
        this.setStatus(this.historyStatus, views.length ? "" : "Products you visit will appear here. Start with a department or this week’s finds below.");
        this.renderSearches(snapshot, this.loadedCards);
        this.updateHistoryControls();
        if (focusedIndex >= 0) {
          const next = this.loadedCards[focusedIndex] || this.loadedCards[focusedIndex - 1];
          const link = next?.item.querySelector(".card-heading a[href]");
          if (link) link.focus({ preventScroll: true });
          else this.focusHistoryHeading();
        }
        if (!this.loadedCards.length && views.length) await this.loadHistoryBatch();
        if (removalGeneration === this.generation && this.isConnected) await this.refreshRelated();
        return;
      }
      this.abortRequests();
      const generation = this.generation;
      this.initialized = true;
      this.scope = snapshot.scope;
      this.nativeStamp = nativeStamp;
      this.lookups.clear();
      this.views = views;
      this.loadedCards = [];
      this.attemptedIds.clear();
      this.loadingHistory = false;
      this.historyGrid?.replaceChildren();
      this.relatedGrid?.replaceChildren();
      if (this.historyGrid) this.historyGrid.hidden = true;
      if (this.relatedGrid) this.relatedGrid.hidden = true;
      this.renderSearches(snapshot);
      this.updateHistoryControls();
      if (!views.length) {
        this.setStatus(this.historyStatus, snapshot.storageAvailable === false ? "Viewed history is unavailable in this browser. You can still explore departments and this week’s products below." : "Products you visit will appear here. Start with a department or this week’s finds below.");
        this.setStatus(this.relatedStatus, "View a few products to discover related ideas here.");
        return;
      }
      this.setStatus(this.relatedStatus, "Finding ideas from your recent views…");
      await this.loadHistoryBatch();
      if (generation === this.generation && this.isConnected) await this.refreshRelated();
    }
  }
  customElements.define("saber-your-recommendations", SaberYourRecommendations);
})();
